import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Mail, MailOpen } from "lucide-react";

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/messages");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const toggleRead = async (m) => {
    try {
      await api.patch(`/messages/${m.id}`, { is_read: !m.is_read });
      load();
      if (selected?.id === m.id) setSelected({ ...m, is_read: !m.is_read });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/messages/${m.id}`);
      toast.success("Deleted.");
      if (selected?.id === m.id) setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-messages">
      <div className="overline mb-3">// Inbox</div>
      <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-10">Messages</h1>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 border border-voltage-blue/30 max-h-[70vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-voltage-muted">No messages yet.</div>
          ) : (
            items.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelected(m)}
                data-testid={`message-row-${m.id}`}
                className={`w-full text-left px-4 py-3 border-b border-voltage-blue/20 hover:bg-voltage-blue/5 transition-colors ${
                  selected?.id === m.id ? "bg-voltage-blue/10" : ""
                } ${m.is_read ? "opacity-70" : ""}`}
              >
                <div className="flex items-center gap-2">
                  {m.is_read ? <MailOpen className="w-4 h-4 text-voltage-muted" /> : <Mail className="w-4 h-4 text-voltage-yellow" />}
                  <span className="font-display font-bold text-white truncate">{m.name}</span>
                </div>
                <div className="text-xs text-voltage-muted mt-1 font-mono truncate">{m.email}</div>
                <div className="text-sm text-white/80 mt-1 truncate">{m.subject || m.message?.slice(0, 50)}</div>
                <div className="text-[10px] text-voltage-muted mt-1 font-mono">
                  {new Date(m.created_at).toLocaleString()}
                </div>
              </button>
            ))
          )}
        </div>

        <div className="lg:col-span-7">
          {selected ? (
            <div className="voltage-card p-6" data-testid="message-detail">
              <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                  <div className="font-display font-black text-2xl text-white">{selected.name}</div>
                  <div className="text-sm text-voltage-muted font-mono mt-1">{selected.email}</div>
                  {selected.subject && <div className="text-voltage-yellow font-display font-bold uppercase tracking-wide mt-3 text-sm">{selected.subject}</div>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => toggleRead(selected)} data-testid="message-toggle-read" className="btn-ghost text-xs">
                    {selected.is_read ? "Mark Unread" : "Mark Read"}
                  </button>
                  <button onClick={() => remove(selected)} data-testid="message-delete" className="btn-ghost text-xs hover:!border-red-500 hover:!text-red-400">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
              <p className="text-white/90 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              <div className="text-xs text-voltage-muted font-mono mt-6 border-t border-voltage-blue/20 pt-4">
                Received {new Date(selected.created_at).toLocaleString()}
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-voltage-blue/30 p-12 text-center text-voltage-muted">
              Select a message to read.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
